import type { EmailBranding } from "@/lib/forms/email-templates";

/** Default closing stored in templates and shown when no custom sign-off is set. */
export const DEFAULT_SIGNOFF_HTML = "Best regards,<br>The Team";

const SIGNOFF_PLACEHOLDER = "{{signoff}}";

/**
 * Below this length, a sign-off line is treated as in-progress typing rather than confirmed
 * text and is never used to strip matches out of the body — a single-letter regex like `/t/gi`
 * would otherwise delete that letter everywhere in the email, not just a duplicated sign-off.
 */
const MIN_SIGNOFF_LINE_LENGTH_FOR_STRIP = 4;

/**
 * How far back from the `{{signoff}}` placeholder {@link stripRenderedSignoffBeforePlaceholder}'s
 * loose per-line match is allowed to reach. A duplicated *rendered* sign-off (the editor
 * round-tripped placeholder → HTML → placeholder and left both) always sits immediately before
 * the placeholder, never scattered through the email's earlier prose — bounding the search window
 * keeps an unlucky text match (e.g. a sign-off line that's also an ordinary word used elsewhere)
 * confined to that trailing block instead of deleting it throughout the whole body.
 */
const SIGNOFF_DEDUPE_WINDOW_CHARS = 800;

const DEFAULT_SIGNOFF_PATTERNS: RegExp[] = [
  /Best regards,?\s*(?:<br\s*\/?>|\n)\s*The Team/gi,
  /<p[^>]*>\s*Best regards,?\s*<\/p>\s*<p[^>]*>\s*The Team\s*<\/p>/gi,
  /Best regards,?\s*<\/p>\s*<p[^>]*>\s*The Team/gi,
];

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceDefaultSignoffPatterns = (body: string, replacement: string): string => {
  let result = body;
  for (const pattern of DEFAULT_SIGNOFF_PATTERNS) {
    result = result.replace(pattern, replacement);
  }
  return result;
};

const collapseSignoffPlaceholders = (body: string): string =>
  body.replace(/(\{\{signoff\}\}\s*)+/g, SIGNOFF_PLACEHOLDER);

/**
 * Builds HTML for the optional sign-off block (text + signature image).
 */
export const buildSignoffHtml = (branding?: EmailBranding): string => {
  const text = branding?.signoffText?.trim();
  const imageUrl = branding?.signoffImageUrl?.trim();
  if (!text && !imageUrl) return "";

  const parts: string[] = [];
  if (imageUrl) {
    parts.push(
      `<div style="margin: 24px 0 8px;"><img src="${imageUrl}" alt="Signature" style="max-height: 96px; max-width: 320px; width: auto; height: auto; display: block;" /></div>`
    );
  }
  if (text) {
    const lines = text
      .split("\n")
      .map((line) => escapeHtml(line))
      .join("<br>");
    parts.push(
      `<p style="margin: ${imageUrl ? "8px" : "24px"} 0 0; font-size: 16px; line-height: 24px; color: #4b5563;">${lines}</p>`
    );
  }
  return parts.join("");
};

const bodyContainsRenderedSignoff = (body: string, branding?: EmailBranding): boolean => {
  const custom = buildSignoffHtml(branding);
  if (custom && body.includes(custom)) return true;

  const text = branding?.signoffText?.trim();
  if (text) {
    const firstLine = text.split("\n")[0]?.trim();
    if (firstLine && (body.includes(escapeHtml(firstLine)) || body.includes(firstLine))) {
      return true;
    }
  }

  for (const pattern of DEFAULT_SIGNOFF_PATTERNS) {
    if (pattern.test(body)) return true;
  }
  return false;
};

/**
 * Removes rendered sign-off HTML that appears before the final {{signoff}} placeholder
 * (avoids duplicate sign-offs when the editor saved expanded HTML plus the placeholder).
 */
const stripRenderedSignoffBeforePlaceholder = (body: string, branding?: EmailBranding): string => {
  if (!body.includes(SIGNOFF_PLACEHOLDER)) return body;

  const lastPlaceholder = body.lastIndexOf(SIGNOFF_PLACEHOLDER);
  let before = body.slice(0, lastPlaceholder);
  const after = body.slice(lastPlaceholder);

  const customHtml = buildSignoffHtml(branding);
  if (customHtml) {
    while (before.includes(customHtml)) {
      before = before.replace(customHtml, "");
    }
  }

  const text = branding?.signoffText?.trim();
  if (text) {
    const windowStart = Math.max(0, before.length - SIGNOFF_DEDUPE_WINDOW_CHARS);
    const head = before.slice(0, windowStart);
    let tail = before.slice(windowStart);
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (trimmed.length < MIN_SIGNOFF_LINE_LENGTH_FOR_STRIP) continue;
      tail = tail.replace(new RegExp(escapeRegExp(escapeHtml(trimmed)), "gi"), "");
      tail = tail.replace(new RegExp(escapeRegExp(trimmed), "gi"), "");
    }
    before = head + tail;
  }

  if (branding?.signoffImageUrl?.trim()) {
    before = before.replace(new RegExp(escapeRegExp(branding.signoffImageUrl.trim()), "g"), "");
  }

  before = replaceDefaultSignoffPatterns(before, "");
  before = before.replace(/(<p[^>]*>\s*<\/p>\s*)+$/gi, "");
  before = before.replace(/(<br\s*\/?>\s*)+$/gi, "");

  return `${before}${after}`;
};

/**
 * HTML shown in the editor / preview (custom sign-off or default "The Team").
 */
export const resolveSignoffForDisplay = (body: string, branding?: EmailBranding): string => {
  const signoffHtml = buildSignoffHtml(branding) || DEFAULT_SIGNOFF_HTML;
  const normalized = stripRenderedSignoffBeforePlaceholder(body, branding);

  if (normalized.includes(SIGNOFF_PLACEHOLDER)) {
    return normalized.replace(/\{\{signoff\}\}/g, signoffHtml);
  }

  return replaceDefaultSignoffPatterns(normalized, signoffHtml);
};

/**
 * Converts editor HTML back to storage ({{signoff}} when using custom branding).
 */
export const normalizeSignoffForStorage = (body: string, branding?: EmailBranding): string => {
  const customHtml = buildSignoffHtml(branding);

  if (customHtml) {
    let result = body;
    if (result.includes(customHtml)) {
      result = result.replaceAll(customHtml, SIGNOFF_PLACEHOLDER);
    }
    result = stripRenderedSignoffBeforePlaceholder(result, branding);
    result = replaceDefaultSignoffPatterns(result, SIGNOFF_PLACEHOLDER);
    return collapseSignoffPlaceholders(result);
  }

  if (body.includes(SIGNOFF_PLACEHOLDER)) {
    return body.replace(/\{\{signoff\}\}/g, DEFAULT_SIGNOFF_HTML);
  }

  return body;
};

/**
 * Applies shared sign-off branding to an email body (replaces {{signoff}} or default closing).
 */
export const applySignoffToBody = (body: string, branding?: EmailBranding): string => {
  const customSignoff = buildSignoffHtml(branding);
  const signoffHtml = customSignoff || DEFAULT_SIGNOFF_HTML;

  if (body.includes(SIGNOFF_PLACEHOLDER)) {
    const deduped = stripRenderedSignoffBeforePlaceholder(body, branding);
    return deduped.replace(/\{\{signoff\}\}/g, signoffHtml);
  }

  if (bodyContainsRenderedSignoff(body, branding)) {
    return body;
  }

  const withReplacedDefault = replaceDefaultSignoffPatterns(body, signoffHtml);
  if (withReplacedDefault !== body) {
    return withReplacedDefault;
  }

  if (customSignoff) {
    return `${body}${customSignoff}`;
  }

  return body;
};

/**
 * Strips the generic default sign-off text ("Best regards,\nThe Team", in any of its rendered HTML
 * forms) from a body, without replacing it with anything. For pipelines where the sign-off is
 * rendered as a separate, locked bookend (never inline body content) — see
 * {@link "@/lib/email/migration/form-legacy-preprocess"}'s `preprocessFormLegacyBody`, which strips
 * the `{{signoff}}` placeholder token but needs this for legacy bodies that already had the default
 * rendered inline (the old editor could persist either shape).
 */
export const stripDefaultSignoffText = (body: string): string => replaceDefaultSignoffPatterns(body, "");

/** Branding fields that apply to every email template type when edited in Design. */
export const SHARED_EMAIL_BRANDING_KEYS = ["signoffText", "signoffImageUrl"] as const;

export type SharedEmailBrandingKey = (typeof SHARED_EMAIL_BRANDING_KEYS)[number];

export const isSharedBrandingUpdate = (updates: Partial<EmailBranding>): boolean =>
  SHARED_EMAIL_BRANDING_KEYS.some((key) => key in updates);
